import { getOriginCustomerId } from "@origin-digital/auth-utils";
import { dapiFetchCache } from "@origin-digital/daxi";
import { fetchAccessToken } from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { identifier } from "@origin-digital/reporting-client";
import { IAppContext } from "@origin-digital/shared-app-context/dist/typings/models";

import { IBrazeStorage, IBrazeInit } from "../consts/interfaces";
import { WHITE_LIST_BRAZE } from "../consts/whitelistBraze";
import { REFERRER_KEY } from "./config";
import { isCookieYoungerThan, readCookies, writeCookies } from "./cookie";
import {
  deleteLastRefreshDate,
  getMatchingStoredBzid,
  getStorageBzid,
  storeBzid,
} from "./storage";

const BRAZE_INDEXED_DB = "AppboyServiceWorkerAsyncStorage";

export const getBrazeIndexedDB = async (): Promise<boolean> => {
  const allIndexedDB = await window.indexedDB.databases();
  return !!allIndexedDB?.find((db) => db.name === BRAZE_INDEXED_DB);
};

export const getAccessToken = async (): Promise<string | undefined> => {
  const cachedJWT = await fetchAccessToken({});
  if (!cachedJWT) {
    return undefined;
  }
  return cachedJWT.accessToken;
};

export const fetchAppContextV2 = async (): Promise<IAppContext | undefined> => {
  const res = await dapiFetchCache<IAppContext>("/api/appcontext/v2/sac", {
    ttlInMin: 60,
    allowStaleData: true,
    authorization: "jwt",
  });
  return res.data;
};

export const isOriginReferrer = (environment: EnvironmentNames) => {
  const { referrer } = document;
  if (!referrer) return false;
  const referrerUrl = new URL(referrer);
  return referrerUrl?.host?.includes(REFERRER_KEY[environment]);
};

export const isWhiteListedPath = (path: string) => {
  const { forceCreateUser } = WHITE_LIST_BRAZE;

  try {
    return !!forceCreateUser.find(
      ({ regex }) => !!regex && path.match(new RegExp(regex))
    );
  } catch {
    return undefined;
  }
};

export const fetchUserServiceBzid = async (): Promise<string | undefined> => {
  const sacContent = await fetchAppContextV2();
  const brazeIdObj = sacContent?.features.find(({ type }) => type === "BRAZE");
  return brazeIdObj?.backendId;
};

export const fetchJwtBrazeId = async (): Promise<IBrazeStorage | undefined> => {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const originId = getOriginCustomerId(accessToken);
    if (!originId) return;

    const matchingStoredBzid = getMatchingStoredBzid(originId);
    if (matchingStoredBzid) return { bzid: matchingStoredBzid, originId };

    const featureBzid = await fetchUserServiceBzid();
    if (featureBzid) return { bzid: featureBzid, originId };
  } catch {}
  return undefined;
};

export const fetchBrazeId = async (
  bzidQueryString: string | undefined,
  environment: EnvironmentNames,
  path: string
): Promise<IBrazeInit | undefined> => {
  // is auth?
  const jwtBzid = await fetchJwtBrazeId();
  if (jwtBzid?.bzid) return jwtBzid;

  // has bzid in URL
  if (bzidQueryString) return { bzid: bzidQueryString };

  const cookie = readCookies();
  if (cookie?.bzid) return cookie;

  // storage check
  const storageBzid = getStorageBzid();
  if (storageBzid.bzid) return storageBzid;

  const hasSdkInit = await getBrazeIndexedDB();
  if (hasSdkInit) return { hasSdkInit };

  const hasOriginReferrer = isOriginReferrer(environment);
  if (hasOriginReferrer) return { hasOriginReferrer };

  // allow whitelisted pages to create user
  const hasWhiteListedPath = isWhiteListedPath(path);
  if (hasWhiteListedPath) return { hasWhiteListedPath };

  return undefined;
};

export const persistUser = (
  brazeObj: IBrazeInit,
  environment: EnvironmentNames
): void => {
  const prevBzid = getStorageBzid().bzid;
  if (prevBzid != null && prevBzid !== brazeObj.bzid) {
    // when user changes ensure we refresh
    deleteLastRefreshDate();
  }
  storeBzid(brazeObj);
  writeCookies(brazeObj, environment);
};

export const syncUserWithAdobe = (brazeObj: IBrazeStorage): void => {
  const oneDay = 1000 * 60 * 60 * 24;

  // Sync with Adobe Analytics if:
  // has bzid and cookie is older than a day, or
  // has bzid and has been created
  if (
    brazeObj.bzid &&
    (!isCookieYoungerThan(oneDay, brazeObj.bzid) || brazeObj.created)
  ) {
    identifier("braze", {
      bzid: brazeObj.bzid,
      ...(brazeObj.created && { created: true }),
    });
  }
};
